// src/components/meetings/RecordingsPage.jsx
// A dedicated page listing every past meeting for the logged-in user, showing
// whether a recording has been attached (matched strictly by meeting_key so it
// can never show the wrong session's video) and letting them watch it directly.

import React, { useState, useEffect, useContext, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import moment from 'moment';
import { FaVideo, FaSpinner, FaSearch, FaCalendarAlt, FaUserTie, FaFileVideo, FaEdit, FaTimes, FaSave } from 'react-icons/fa';
import { UserContext } from '../../context/UserContext';
import { API } from '../../config';
import styles from './RecordingsPage.module.css';
import Sidebar from './Sidebar';

const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.05 } },
};

const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
};

const RecordingsPage = () => {
    const user = useContext(UserContext);
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [onlyWithRecording, setOnlyWithRecording] = useState(false);
    const [editingMeeting, setEditingMeeting] = useState(null); // meeting object, or null when modal closed
    const [urlInput, setUrlInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState('');

    const isStaff = user && ['admin', 'superAdmin', 'trainer', 'employee'].includes(user.role);
    const showSidebar = isStaff; // staff already navigate via the meetings sidebar

    const openEditModal = (meeting) => {
        setEditingMeeting(meeting);
        setUrlInput(meeting.recording_play_url || '');
        setModalError('');
    };

    const closeEditModal = () => {
        setEditingMeeting(null);
        setUrlInput('');
        setModalError('');
    };

    const handleSaveUrl = async () => {
        if (!editingMeeting) return;
        if (!urlInput.trim()) {
            setModalError('Please paste the recording URL.');
            return;
        }
        setSaving(true);
        setModalError('');
        try {
            const response = await fetch(API.SET_RECORDING_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    meetingKey: editingMeeting.meeting_key,
                    recordingUrl: urlInput.trim(),
                }),
            });
            const result = await response.json();
            if (result.success) {
                setMeetings(prev => prev.map(m =>
                    m.meeting_key === editingMeeting.meeting_key
                        ? { ...m, recording_play_url: result.playUrl }
                        : m
                ));
                closeEditModal();
            } else {
                setModalError(result.message || 'Failed to save recording URL.');
            }
        } catch {
            setModalError('Could not reach the server. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        const fetchMeetings = async () => {
            if (!user || !user.email) {
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(
                    `${API.GET_USER_MEETINGS_ENDPOINT}?email=${encodeURIComponent(user.email)}&role=${encodeURIComponent(user.role || '')}`
                );
                const data = await response.json();
                if (data.success) {
                    const now = new Date();
                    const past = (data.sessions || [])
                        .filter(m => new Date(m.start_time) < now)
                        .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
                    setMeetings(past);
                } else {
                    setError(data.message || 'Failed to load recordings.');
                }
            } catch (err) {
                setError('Failed to load recordings.');
            } finally {
                setLoading(false);
            }
        };
        fetchMeetings();
    }, [user]);

    const filteredMeetings = useMemo(() => {
        return meetings.filter(m => {
            if (onlyWithRecording && !m.recording_play_url) return false;
            if (search && !m.topic?.toLowerCase().includes(search.toLowerCase())) return false;
            return true;
        });
    }, [meetings, search, onlyWithRecording]);

    const recordedCount = meetings.filter(m => m.recording_play_url).length;

    return (
        <div className={styles.withSidebar}>
            {showSidebar && <Sidebar />}
            <div className={styles.container}>
                <motion.div
                    className={styles.card}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className={styles.headerRow}>
                        <h2 className={styles.title}>
                            <FaFileVideo /> Meeting Recordings
                        </h2>
                        <span className={styles.countPill}>
                            {recordedCount} of {meetings.length} available
                        </span>
                    </div>

                    <div className={styles.controlsRow}>
                        <div className={styles.searchBox}>
                            <FaSearch />
                            <input
                                type="text"
                                placeholder="Search by topic..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                        <label className={styles.filterToggle}>
                            <input
                                type="checkbox"
                                checked={onlyWithRecording}
                                onChange={e => setOnlyWithRecording(e.target.checked)}
                            />
                            Only show meetings with a recording
                        </label>
                    </div>

                    <AnimatePresence mode="wait">
                        {loading && (
                            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={styles.loader}>
                                <FaSpinner className={styles.spinner} />
                                <p>Loading recordings...</p>
                            </motion.div>
                        )}

                        {!loading && error && (
                            <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={styles.errorMessage}>
                                <p>{error}</p>
                            </motion.div>
                        )}

                        {!loading && !error && filteredMeetings.length === 0 && (
                            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={styles.emptyState}>
                                <FaFileVideo />
                                <h4>No recordings to show</h4>
                                <p>
                                    {meetings.length === 0
                                        ? "You don't have any past meetings yet."
                                        : 'Try adjusting your search or filter.'}
                                </p>
                            </motion.div>
                        )}

                        {!loading && !error && filteredMeetings.length > 0 && (
                            <motion.ul
                                key="list"
                                className={styles.recordingList}
                                variants={staggerContainer}
                                initial="hidden"
                                animate="visible"
                            >
                                {filteredMeetings.map(meeting => (
                                    <motion.li
                                        key={meeting.meeting_key}
                                        className={styles.recordingItem}
                                        variants={fadeUp}
                                        whileHover={{ y: -2, transition: { duration: 0.15 } }}
                                    >
                                        <div className={styles.recordingInfo}>
                                            <h4 className={styles.recordingTitle}>{meeting.topic}</h4>
                                            <div className={styles.recordingMeta}>
                                                <span><FaCalendarAlt /> {moment(meeting.start_time).format('MMM Do YYYY, h:mm A')}</span>
                                                {meeting.presenter_email && (
                                                    <span><FaUserTie /> {meeting.presenter_email}</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className={styles.actionsCol}>
                                            {meeting.recording_play_url ? (
                                                <a
                                                    href={meeting.recording_play_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.watchButton}
                                                >
                                                    <FaVideo /> Watch Recording
                                                </a>
                                            ) : (
                                                <span className={styles.unavailableLabel}>Not available yet</span>
                                            )}

                                            {isStaff && (
                                                <button
                                                    className={styles.editUrlButton}
                                                    onClick={() => openEditModal(meeting)}
                                                >
                                                    <FaEdit /> {meeting.recording_play_url ? 'Edit' : 'Add'} URL
                                                </button>
                                            )}
                                        </div>
                                    </motion.li>
                                ))}
                            </motion.ul>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>

            <AnimatePresence>
                {editingMeeting && (
                    <motion.div
                        className={styles.modalOverlay}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeEditModal}
                    >
                        <motion.div
                            className={styles.modalCard}
                            initial={{ opacity: 0, y: 20, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 20, scale: 0.96 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className={styles.modalHeader}>
                                <h3 className={styles.modalTitle}>
                                    <FaVideo /> Attach Recording
                                </h3>
                                <button onClick={closeEditModal} className={styles.modalCloseBtn} aria-label="Close">
                                    <FaTimes />
                                </button>
                            </div>

                            <p className={styles.modalMeetingTopic}>{editingMeeting.topic}</p>

                            <label className={styles.modalLabel}>
                                Recording URL
                                <input
                                    type="text"
                                    autoFocus
                                    placeholder="Paste the Zoho recording link here"
                                    value={urlInput}
                                    onChange={(e) => setUrlInput(e.target.value)}
                                    className={styles.modalInput}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveUrl(); }}
                                />
                            </label>

                            {modalError && <p className={styles.modalErrorText}>{modalError}</p>}

                            <div className={styles.modalActions}>
                                <button onClick={closeEditModal} className={styles.modalCancelBtn} disabled={saving}>
                                    Cancel
                                </button>
                                <button onClick={handleSaveUrl} className={styles.modalSaveBtn} disabled={saving}>
                                    {saving ? <FaSpinner className={styles.spinner} /> : <FaSave />}
                                    {' '}Save
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default RecordingsPage;
